/**
 * System health dashboard — DB status, queue depth, cost run-rate.
 */
import { useState, useEffect } from 'react';
import { api } from '../../api';
import ModelSettings from './ModelSettings';
import TierOneSwitch from './TierOneSwitch';

export default function SystemDashboard({ opsRole }) {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [loading, setLoading] = useState(true);

  async function fetchAll() {
    setLoading(true);
    await Promise.all([
      api.get('/ops_admin/system/health').then(setHealth).catch(() => {}),
      api.get('/ops_admin/system/metrics').then(setMetrics).catch(() => {}),
      api.get('/ops_admin/system/incidents').then(setIncidents).catch(() => {}),
    ]);
    setLoading(false);
  }

  useEffect(() => { fetchAll(); }, []);

  if (loading) return <p className="loading-text">Loading system status...</p>;

  return (
    <div className="ops-system-dashboard">
      <div className="ops-page-header">
        <h1>System Dashboard</h1>
        <button className="btn btn-sm btn-ghost" onClick={fetchAll}>Refresh</button>
      </div>

      {health && (
        <div className="ops-section">
          <h2>
            Health
            <span className={`badge ml-2 badge-${health.status === 'healthy' ? 'active' : 'suspended'}`}>
              {health.status}
            </span>
          </h2>
          <div className="info-grid">
            <div className="info-item"><label>Database</label><span>{health.checks?.database}</span></div>
            <div className="info-item"><label>Cost run-rate</label><span>${(health.cost_run_rate_usd || 0).toFixed(2)} ({health.billing_period})</span></div>
          </div>
          {health.document_queue && Object.keys(health.document_queue).length > 0 && (
            <div className="queue-table">
              <h3>Document Queue</h3>
              <table className="ops-table ops-table-sm">
                <thead><tr><th>Status</th><th>Count</th></tr></thead>
                <tbody>
                  {Object.entries(health.document_queue).map(([status, count]) => (
                    <tr key={status}><td>{status}</td><td>{count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {metrics && (
        <div className="ops-section">
          <h2>Metrics — {metrics.period}</h2>
          <div className="info-grid">
            <div className="info-item"><label>Total orgs</label><span>{metrics.orgs?.total ?? '—'}</span></div>
            <div className="info-item"><label>Active orgs</label><span>{metrics.orgs?.active ?? '—'}</span></div>
            <div className="info-item"><label>Active this period</label><span>{metrics.orgs?.active_this_period ?? '—'}</span></div>
          </div>
          {metrics.cost_by_service?.length > 0 && (
            <table className="ops-table">
              <thead><tr><th>Service</th><th>Total (USD)</th><th>Events</th></tr></thead>
              <tbody>
                {metrics.cost_by_service.map(s => (
                  <tr key={s.service}>
                    <td>{s.service}</td>
                    <td>${(s.total_usd || 0).toFixed(4)}</td>
                    <td>{s.event_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <ModelSettings opsRole={opsRole} />
      <TierOneSwitch opsRole={opsRole} />

      {incidents && (
        <div className="ops-section">
          <h2>Incidents — last {incidents.window_hours}h</h2>
          {incidents.incidents?.length === 0 ? (
            <p className="success-text">No incidents</p>
          ) : (
            <table className="ops-table">
              <thead><tr><th>Status</th><th>Count</th></tr></thead>
              <tbody>
                {incidents.incidents.map(i => (
                  <tr key={i.status}><td>{i.status}</td><td>{i.count}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
